import {
  useLazyPostRedoChronologyQuery,
  useLazyPostRedoTimelineQuery,
  useLazyPostUndoChronologyQuery,
  useLazyPostUndoTimelineQuery,
} from '@/api';
import {
  selectCaseIdData,
  selectSharedDataActiveTimelineChronologyType,
  selectTimelineChronologyUndoRedoHistory,
  TimelineChronologyType,
  useAppSelector,
} from '@/store';
import { BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, useFletchersBroadcast } from '@/broadcast';
import { Button, RedoIcon, UndoIcon } from '@repo/atomic-components';
import { classNames } from 'primereact/utils';
import { useCallback } from 'react';

export const TimelineChronologyUndoRedoButtons = () => {
  const caseId = useAppSelector(selectCaseIdData);
  const activeTimelineChronologyType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const timelineChronologyUndoRedoHistory = useAppSelector(selectTimelineChronologyUndoRedoHistory);

  const { sendMessageWithoutResponse } = useFletchersBroadcast();

  const [triggerPostUndoTimeline] = useLazyPostUndoTimelineQuery();
  const [triggerPostRedoTimeline] = useLazyPostRedoTimelineQuery();
  const [triggerPostUndoChronology] = useLazyPostUndoChronologyQuery();
  const [triggerPostRedoChronology] = useLazyPostRedoChronologyQuery();

  const handleUndoClick = useCallback(() => {
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      triggerPostUndoTimeline({ caseId: caseId });
    }
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      triggerPostUndoChronology({ caseId: caseId })
        .unwrap()
        // Undo can add or remove chronology entries; we don't know which, so the
        // Bundle tabs fully refresh (no entryIds means "refetch, don't optimise").
        .then(() => sendMessageWithoutResponse(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, {}))
        .catch(() => {});
    }
  }, [
    activeTimelineChronologyType,
    caseId,
    sendMessageWithoutResponse,
    triggerPostUndoChronology,
    triggerPostUndoTimeline,
  ]);

  const handleRedoClick = useCallback(() => {
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.TIMELINE) {
      triggerPostRedoTimeline({ caseId: caseId });
    }
    if (caseId && activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY) {
      triggerPostRedoChronology({ caseId: caseId })
        .unwrap()
        .then(() => sendMessageWithoutResponse(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, {}))
        .catch(() => {});
    }
  }, [
    activeTimelineChronologyType,
    caseId,
    sendMessageWithoutResponse,
    triggerPostRedoChronology,
    triggerPostRedoTimeline,
  ]);

  return (
    <div
      className={classNames('border-fletchers-gray-110 flex h-full max-h-[32px] overflow-hidden rounded-[4px] border')}
    >
      <Button
        icon={<UndoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
        onClick={handleUndoClick}
        outlined
        className="!border-r-fletchers-gray-110 !h-[32px] !w-[32px] rounded-none! !border-0 !border-r !border-solid !px-[9px] !py-[9px] hover:!bg-gray-50"
        disabled={!timelineChronologyUndoRedoHistory || timelineChronologyUndoRedoHistory.applied === 0}
      />
      <Button
        icon={<RedoIcon className="h-[14px] w-[14px] -translate-y-px text-black" />}
        onClick={handleRedoClick}
        outlined
        className="!h-[32px] !w-[32px] rounded-none! !border-0 !px-[9px] !py-[9px] hover:!bg-gray-50"
        disabled={!timelineChronologyUndoRedoHistory || timelineChronologyUndoRedoHistory.undone === 0}
      />
    </div>
  );
};
